import { Component, computed, output, inject, signal, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AppState } from 'src/app/app.state';
import { PtsPaymentTerminal, TerminalConnectionStatus, TerminalStatus } from '../pts-payment-terminal-model';
import { PaymentTerminalSharedService } from 'src/app/shared/services/payment-terminal.shared.service';
import { PayterTerminalLocation } from '../pts-payment-terminal-model';
import { PaymentTerminalActionsComponent } from '../../payment-terminal-actions/payment-terminal-actions.component';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { TranslateTerminalStatusEnumPipe } from 'src/app/shared/pipes/translate-terminal-status-enum.pipe';
import { TranslateTerminalConnectionStatusEnumPipe } from 'src/app/shared/pipes/translate-terminal-connection-status-enum.pipe';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { AdminEnumSelectListMultipleComponent } from 'src/app/shared/components/admin-enum-select-list-multiple/admin-enum-select-list-multiple.component';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { PtsTerminalsListStateService } from './pts-terminal-list-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminPayterLocationsTypeAheadComponent } from 'src/app/shared/components/admin-payter-locations-type-ahead/admin-payter-locations-type-ahead.component';
import {
  CardComponent,
  ExpandListComponent,
  ExpandListConfiguration,
  ExpandListSort,
  ScreenSizeEnum,
  SnackbarService,
} from 'ok-angular-shared/components';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { PtsTerminalConnectorRelationDialogComponent } from '../dialogs/terminal-connector-relation-dialog/terminal-connector-relation-dialog.component';
import { TerminalStatusDialogComponent } from '../dialogs/terminal-status-dialog/terminal-status-dialog.component';
import { TerminalUiLogsViewerDialogComponent } from '../dialogs/terminal-ui-logs-viewer-dialog/terminal-ui-logs-viewer-dialog.component';
import { TerminalEmulatorDialogComponent } from '../dialogs/terminal-emulator-dialog/terminal-emulator-dialog.component';
import { TerminalSessionsDialogComponent } from '../dialogs/terminal-sessions-dialog/terminal-sessions-dialog.component';

@Component({
  selector: 'adm-pts-overview',
  templateUrl: './pts-overview.component.html',
  styleUrl: './pts-overview.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    PaymentTerminalActionsComponent,
    AdminEnumSelectListComponent,
    MatButton,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AdminEnumSelectListMultipleComponent,
    TextSearchComponent,
    AdminPayterLocationsTypeAheadComponent,
    CardComponent,
    ExpandListComponent,
    MatPaginator,
    MatTooltip,
    AsyncPipe,
    DatePipe,
    RouterLink,
    TranslateTerminalStatusEnumPipe,
  ],
  providers: [TranslateTerminalConnectionStatusEnumPipe, TranslateTerminalStatusEnumPipe, PtsTerminalsListStateService],
})
export class PtsOverviewComponent {
  private paymentTerminalSharedService = inject(PaymentTerminalSharedService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  public roles = this.environmentLoaderService.virtualRoles;
  public sessionsView = signal(this.router.url.includes('/sessions'));

  state = inject(PtsTerminalsListStateService);
  appState = inject(AppState);
  snackbarService = inject(SnackbarService);
  translateConnectonStatusPipe = inject(TranslateTerminalConnectionStatusEnumPipe);
  translateTerminalStatusEnum = inject(TranslateTerminalStatusEnumPipe);
  refreshAfterUpdateEvent = output<PtsPaymentTerminal>();
  refreshAfterDeleteEvent = output<string>();
  allStatuses: TerminalStatus[] = Object.values(TerminalStatus);
  allConnectionStatuses: TerminalConnectionStatus[] = Object.values(TerminalConnectionStatus);
  isReady = false;
  private refreshOverviewSubscription = this.paymentTerminalSharedService
    .getRefreshEventEmitter()
    .pipe(
      tap(paymentTerminal => {
        this.state.refreshTerminal(paymentTerminal as PtsPaymentTerminal);
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  private readonly canWriteTerminal = this.authService.hasRole(this.roles[Permission.PayterPaymentTerminalWrite] ?? '');
  private readonly canWriteExtendedTerminal = this.authService.hasRole(this.roles['payter-payment-terminal-extended.write'] ?? '');
  private readonly canReadTerminal = this.authService.hasRole(this.roles[Permission.PayterPaymentTerminalRead] ?? '');

  currentSort = signal<ExpandListSort<PtsPaymentTerminal>>({ column: 'name', direction: 'asc' });

  onlineStatusColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('onlineStatusColumnTemplate');
  statusColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('statusColumnTemplate');
  locationColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('locationColumnTemplate');
  connectorCountColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('connectorCountColumnTemplate');
  terminalSessionCountColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('terminalSessionCountColumnTemplate');
  createdColumnTemplate = viewChild<TemplateRef<PtsPaymentTerminal>>('createdColumnTemplate');

  public config = computed<ExpandListConfiguration<PtsPaymentTerminal>>(() => {
    this.state.terminals();

    return {
      expandDisabled: true,
      textLineClamp: 2,
      badgeFn: terminal => (terminal.hasConnectorsFromMultipleOrganizations ? '!' : ''),
      tooltipFn: terminal =>
        terminal.hasConnectorsFromMultipleOrganizations
          ? `De ${terminal.connectorCount} ladepunkter er tilknyttet flere organisationer. Dette er sandsynligvis en fejl, som bør rettes.`
          : '',
      classFn: (terminal: PtsPaymentTerminal) => {
        const classes: string[] = [];

        if (terminal.hasConnectorsFromMultipleOrganizations) {
          classes.push('notification--error');
        }
        if (terminal.highlight?.getValue()) {
          classes.push('highlight');
        }

        return classes.join(' ');
      },
      columns: [
        { field: 'online', header: '', width: '50px', disableSort: true, displayTemplate: this.onlineStatusColumnTemplate() },
        { field: 'name', header: 'Navn', width: '200px', sortField: 'name' },
        { field: 'statuses', header: 'Status', width: '3fr', disableSort: true, displayTemplate: this.statusColumnTemplate() },
        { field: 'serialNumber', header: 'Serienummer', width: '1fr', sortField: 'serialNumber', visibleFrom: ScreenSizeEnum.md },
        {
          field: 'chargingStationLocationInfo',
          header: 'Lokation',
          width: '1.5fr',
          disableSort: true,
          displayTemplate: this.locationColumnTemplate(),
        },
        {
          field: 'connectorCount',
          header: 'Ladepunkter',
          width: '150px',
          disableSort: true,
          displayTemplate: this.connectorCountColumnTemplate(),
        },
        {
          field: 'terminalSessionCount',
          header: 'Sessioner',
          width: '150px',
          disableSort: true,
          displayTemplate: this.terminalSessionCountColumnTemplate(),
        },
        {
          field: 'created',
          header: 'Oprettet',
          width: '1fr',
          sortField: 'updatedBy',
          displayTemplate: this.createdColumnTemplate(),
          visibleFrom: ScreenSizeEnum.md,
        },
      ],
      hideActionsFn: () => !this.canWriteTerminal && !this.canWriteExtendedTerminal && !this.canReadTerminal,
      actions: [
        {
          svgIcon: 'socket_type_ccs2',
          label: 'Tilknyt ladepunkter',
          action: terminal => terminal && this.terminalConnectorRelation(terminal),
          hiddenFn: () => !this.canWriteTerminal,
        },
        {
          icon: 'adjust',
          label: 'Angiv terminalstatus',
          action: terminal => terminal && this.terminalStatus(terminal),
          hiddenFn: () => !this.canWriteTerminal,
        },
        {
          icon: 'videogame_asset',
          label: 'Terminalstyring',
          action: terminal => terminal && this.terminalEmulator(terminal),
          hiddenFn: () => !this.canWriteExtendedTerminal,
        },
        {
          icon: 'tv',
          label: 'Skærmbilleder',
          action: terminal => terminal && this.terminalUiLogs(terminal),
          hiddenFn: () => !this.canWriteTerminal,
        },
        {
          icon: 'list',
          label: 'Sessions log',
          action: terminal => terminal && this.terminalSessions(terminal),
          hiddenFn: () => !this.canReadTerminal,
        },
      ],
    };
  });

  locationSelected = (location: PayterTerminalLocation | null) => {
    this.state.ToFirstPage({
      chargingStationLocationIdentifier: location?.chargingStationLocationIdentifier,
    });
  };

  updateConnectionStatus(value: TerminalConnectionStatus | null) {
    this.state.ToFirstPage({ connectionStatus: value });
  }

  updateStatuses(value: TerminalStatus[] | null) {
    this.state.ToFirstPage({ statuses: value ?? [] });
  }
  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.ToFirstPage({ query: value });
    });
  };

  public async refreshAfterUpdate(paymentTerminal: PtsPaymentTerminal) {
    this.state.refreshTerminal(paymentTerminal);
  }

  public resetFilter() {
    this.state.resetFilter();
  }

  public resetLocationFilter() {
    this.state.form.controls.chargingStationLocationIdentifier.setValue(null);
  }

  public pageSwitch(event: PageEvent) {
    this.state.updatePage(event);
  }

  public onSortChange(sort: ExpandListSort<PtsPaymentTerminal>) {
    this.currentSort.set(sort);

    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    this.state.updateSort(matSort);
  }

  public terminalConnectorRelation(paymentTerminal: PtsPaymentTerminal) {
    const dialogRef = this.dialog.open(PtsTerminalConnectorRelationDialogComponent, {
      data: { paymentTerminal },
      width: '760px',
      minHeight: '420px',
    });

    dialogRef.afterClosed().subscribe((paymentTerminal: PtsPaymentTerminal) => {
      if (paymentTerminal) {
        this.refreshAfterUpdate(paymentTerminal);
      }
    });
  }

  public terminalStatus(paymentTerminal: PtsPaymentTerminal) {
    const dialogRef = this.dialog.open(TerminalStatusDialogComponent, {
      data: { paymentTerminal },
      width: '725px',
      minHeight: '550px',
    });

    dialogRef.afterClosed().subscribe((paymentTerminal: PtsPaymentTerminal) => {
      if (paymentTerminal) {
        this.refreshAfterUpdate(paymentTerminal);
      }
    });
  }

  public terminalUiLogs(paymentTerminal: PtsPaymentTerminal) {
    this.dialog.open(TerminalUiLogsViewerDialogComponent, {
      data: { paymentTerminal },
      width: '1024px',
      minHeight: '420px',
    });
  }

  public terminalEmulator(paymentTerminal: PtsPaymentTerminal) {
    this.dialog.open(TerminalEmulatorDialogComponent, {
      data: { paymentTerminal },
      width: '1024px',
      minHeight: '650px',
    });
  }

  public terminalSessions(paymentTerminal: PtsPaymentTerminal) {
    this.dialog.open(TerminalSessionsDialogComponent, {
      data: { paymentTerminal },
      width: '1280px',
    });
  }
}
